import { Close } from "@mui/icons-material";
import {
  Box,
  Button,
  CircularProgress,
  Drawer,
  IconButton,
  Link,
  Stack,
  Typography,
} from "@mui/joy";
import { useNavigate } from "@tanstack/react-router";
import {
  ApiLeagueCompetitionScorecard,
  ApiLeagueEntry,
  ApiLeagueEntryInline,
} from "griptonite-api-client";

import FlashedCircle from "@/assets/icons/FlashedCircle";
import TickedCircle from "@/assets/icons/TickedCircle";
import ZonedCircle from "@/assets/icons/ZonedCircle";
import { useCurrentLeagueUuid, useLeagueEntryQuery, useLeagueQuery } from "@/hooks/competitions";
import formatNumber, { getNumberWithOrdinal } from "@/utils/formatNumber";

import Avatar from "../Avatar";
import { RouteCircle } from "../RouteCircle";
import { RouteNameText } from "../RoutesTable/RouteName";
import { useLeagueEntriesTableContext } from "./LeagueEntriesContext";

const InfoBox = ({ label, value }: { label: string; value: string }) => (
  <Stack alignItems="center">
    <Typography level="body-sm" lineHeight={1.2}>
      {label}
    </Typography>
    <Box
      sx={{
        backgroundColor: "neutral.100",
        paddingX: 0.5,
        paddingY: 0.5,
        borderRadius: 8,
        minWidth: 50,
        textAlign: "center",
        marginTop: 0.25,
      }}
    >
      <Typography level="body-sm" fontWeight="bold">
        {value}
      </Typography>
    </Box>
  </Stack>
);

const Header = ({ entry }: { entry?: ApiLeagueEntryInline | ApiLeagueEntry }) => {
  const leagueUuid = useCurrentLeagueUuid();
  const { data: league } = useLeagueQuery(leagueUuid);
  const category =
    typeof entry?.category === "object"
      ? entry.category
      : league?.categories.find((c) => c.uuid === entry?.category);

  const position = entry?.position ?? null;
  const positionOrdinal = position ? getNumberWithOrdinal(position) : null;
  const setMergeSourceUserUuid = useLeagueEntriesTableContext(
    (state) => state.setMergeSourceUserUuid,
  );

  const navigate = useNavigate();

  // const { display: displayModal } = useModal();

  return (
    <Stack>
      <Stack direction="row" alignItems="center" sx={{ padding: 1 }} spacing={1}>
        <Avatar user={entry?.user} size={48} />
        <Stack sx={{ flex: 1 }}>
          <Typography level="title-md">
            {entry?.user.firstName} {entry?.user.lastName}
          </Typography>
          {entry?.user.email && !entry.user.email.match(/@signup.griptonite.io$/) ? (
            <Typography level="body-sm" fontSize="0.82rem" lineHeight={1} marginBottom={0.25}>
              <Link href={`mailto:${entry.user.email}`}>{entry.user.email}</Link>
            </Typography>
          ) : null}
          <Typography level="title-sm">{category?.name}</Typography>
        </Stack>
        <IconButton
          onClick={() =>
            navigate({
              to: "..",
              from: "/gym/$venueUuid/events/series/$leagueUuid/competitors/$userUuid",
              replace: true,
            })
          }
        >
          <Close />
        </IconButton>
      </Stack>
      {entry ? (
        <Stack
          sx={{
            padding: 1,
            paddingTop: 0,
            borderBottom: "1px solid",
            borderBottomColor: "neutral.100",
          }}
          direction="row"
          spacing={1.5}
          alignItems="center"
        >
          <InfoBox label="Position" value={positionOrdinal || "-"} />
          <InfoBox label="Score" value={formatNumber(entry.score) + "pts"} />
          <Box sx={{ flex: 1 }} />
          <Button
            color="neutral"
            variant="soft"
            size="sm"
            onClick={() => {
              setMergeSourceUserUuid(entry.user.uuid);
              navigate({
                to: "..",
                from: "/gym/$venueUuid/events/series/$leagueUuid/competitors/$userUuid",
                replace: true,
              });
            }}
          >
            Merge with...
          </Button>
        </Stack>
      ) : null}
    </Stack>
  );
};

const AttemptsText = ({
  attempts,
  success,
  segmentsCount,
}: {
  attempts: number;
  success: boolean;
  segmentsCount?: number;
}) => {
  if (segmentsCount) {
    return `Zone ${segmentsCount} (${getNumberWithOrdinal(attempts)} try)`;
  } else if (!success) {
    return `${attempts} ${attempts === 1 ? "try" : "tries"}`;
  }
  return `${getNumberWithOrdinal(attempts)} try`;
};

const RoundScorecard = ({ round }: { round: ApiLeagueCompetitionScorecard }) => {
  const scorecard = round.stageScorecards[0];
  const competition = round.competition;
  const scorecardRoutes = scorecard.venueGroups[0].routes;
  if (!scorecard) {
    return;
  }
  return (
    <Stack>
      <Stack
        direction="row"
        justifyContent="space-between"
        sx={{
          padding: 1,
          opacity: round.contributesToLeagueScore ? 1 : 0.5,
        }}
        spacing={2}
      >
        <Typography level="title-md" fontWeight="bold" flex={1}>
          {competition.name}
        </Typography>
        <Typography level="title-md" fontWeight="bold">
          {round.position ? getNumberWithOrdinal(round.position) : null}
        </Typography>
        <Typography level="title-md" fontWeight="bold">
          {formatNumber(scorecard.score, { maxDecimals: 1 })}pts
        </Typography>
      </Stack>
      <Stack>
        {scorecardRoutes.map((scorecardRoute, index) => {
          if (!scorecardRoute.score && !scorecardRoute.count) {
            return null;
          }
          return (
            <Stack
              key={index}
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{
                borderBottom: "1px solid",
                borderBottomColor: "neutral.100",
                marginLeft: "-8px",
                marginRight: "-8px",
                paddingX: 2,
                paddingY: 0.25,
              }}
            >
              <Stack direction={"row"} spacing={0.5} alignItems="center" sx={{ width: 80 }}>
                <RouteCircle
                  colors={(scorecardRoute.route.colors || "#FFFFFF").split(":")}
                  size={18}
                />
                <Typography level="body-sm" fontWeight="bold">
                  <RouteNameText
                    route={scorecardRoute.route}
                    competitionCategoryUuid={round.category}
                  />
                </Typography>
              </Stack>
              <Stack sx={{ flex: 1 }} direction="row" spacing={0.5} alignItems="center">
                {scorecardRoute.success ? (
                  scorecardRoute.count === 1 ? (
                    <FlashedCircle sx={{ fontSize: 14 }} />
                  ) : (
                    <TickedCircle sx={{ fontSize: 14 }} />
                  )
                ) : scorecardRoute.segmentsCount ? (
                  <ZonedCircle sx={{ fontSize: 14 }} />
                ) : (
                  <Box
                    sx={{
                      width: 14,
                      height: 14,
                      borderRadius: 7,
                      border: "1px solid",
                      borderColor: "neutral.600",
                    }}
                  />
                )}
                <Typography level="body-sm">
                  <AttemptsText
                    attempts={scorecardRoute.count}
                    success={scorecardRoute.success}
                    segmentsCount={scorecardRoute.segmentsCount ?? 0}
                  />
                </Typography>
              </Stack>
              <Typography level="body-sm">
                {formatNumber(scorecardRoute.score, { maxDecimals: 1 })}pts
              </Typography>
            </Stack>
          );
        })}
      </Stack>
    </Stack>
  );
};

const LeagueSidebarContent = ({
  entryQuery,
}: {
  entryQuery: ReturnType<typeof useLeagueEntryQuery>;
}) => {
  const entry = entryQuery.data;

  if (!entry || !("entries" in entry)) {
    return (
      <Box
        sx={{
          flex: 1,
          display: "flex",
          justifyContent: "center",
          paddingTop: "150px",
        }}
      >
        <CircularProgress thickness={4} />
      </Box>
    );
  }

  return (
    <Stack
      sx={{
        padding: 1,
        gap: 1,
        overflowY: "auto",
        height: "100%",
      }}
    >
      {entry?.entries.map((roundEntry) => (
        <RoundScorecard key={roundEntry.competition.uuid} round={roundEntry} />
      ))}
    </Stack>
  );
};

export default function LeagueCompetitorSidebar({
  leagueUuid,
  userUuid,
}: {
  leagueUuid: string;
  userUuid: string;
}) {
  const entryQuery = useLeagueEntryQuery(leagueUuid, userUuid);
  return (
    <Drawer open anchor="right" sx={{ ".MuiDrawer-content": { maxWidth: 480 } }}>
      <Header entry={entryQuery.data} />
      <LeagueSidebarContent entryQuery={entryQuery} />
    </Drawer>
  );
}
